import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, ArrowsClockwise, Download, Lightning, MusicNote, BookOpen, CurrencyGbp, Lightning as LightningIcon, CheckCircle, Ticket, Star, Sparkle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { BackToTop } from '../components/BackToTop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HowTo = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin);

  useEffect(() => {
    axios.post(`${API}/track-visit`, { path: '/how-to' }).catch(() => {});
  }, []);

  const steps = [
    {
      icon: FileText,
      title: 'Upload or paste your text',
      detail: 'Click Upload to import a TXT, DOCX, PDF, FDX, or Fountain file, or paste your text directly into the Original pane. If your file is not uploading, simply copy and paste the text into the Original pane instead.',
    },
    {
      icon: ArrowsClockwise,
      title: 'Press Proofread and wait',
      detail: 'Choose your document type (Manuscript, Screenplay, or Song), AI model, priority, and style preset, then click Proofread. Allow your work a moment to be processed.',
    },
    {
      icon: Lightning,
      title: 'Review the highlighted corrections',
      detail: 'Only the text that has been corrected will be highlighted — additions appear in muted sage, removals in faded rose. Everything else is your original text, untouched.',
    },
    {
      icon: Download,
      title: 'Download your clean file',
      detail: 'When you press TXT (or DOCX), your text will be downloaded to your hard drive without the highlight markings — a fresh, clean file with the corrections applied.',
    },
  ];

  return (
    <div className="w-full min-h-screen bg-background" data-testid="howto-page">
      <header className="h-16 border-b border-border flex items-center justify-between px-6 md:px-10 bg-background">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            className="rounded-lg font-medium text-xs uppercase tracking-[0.2em] px-4 py-2 bg-transparent text-ivory border border-border hover:border-gold hover:text-gold"
            data-testid="back-btn"
          >
            <ArrowLeft size={14} weight="duotone" className="mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-serif font-medium tracking-tight text-ivory">How To Use</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24 animate-fade-up">
        <div className="mb-14">
          <p className="text-[11px] font-medium uppercase tracking-[0.32em] text-ash mb-4">Quick Start Guide</p>
          <h2 className="text-5xl md:text-6xl font-serif font-light tracking-tight text-ivory leading-[1.05] mb-6">
            Four steps to <em className="text-gold not-italic font-normal">polished</em> text.
          </h2>
          <p className="text-base text-bone font-sans leading-relaxed max-w-xl">
            Manuscript Polish is designed to be fast and unobtrusive. Here's how to get the most from it in under a minute.
          </p>
        </div>

        <ol className="space-y-12">
          {steps.map((step, i) => (
            <li key={step.title} className="grid grid-cols-[auto_1fr] gap-8" data-testid={`step-${i + 1}`}>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-lg border border-border bg-card flex items-center justify-center">
                  <step.icon size={20} weight="duotone" className="text-gold" />
                </div>
                <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-ash mt-3">{String(i + 1).padStart(2, '0')}</div>
              </div>
              <div className="pt-1">
                <h3 className="text-2xl font-serif font-normal text-ivory tracking-tight mb-3">{step.title}</h3>
                <p className="text-base text-bone font-sans leading-[1.8]">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-16 panel-luxe p-7" data-testid="note-highlights">
          <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-gold mb-3">A note on highlights</p>
          <p className="text-sm text-bone font-sans leading-[1.8]">
            The highlighted view exists so you can see exactly what changed and decide whether to keep each edit. Your downloaded file is always clean — no markup, no markings, just your improved text.
          </p>
        </div>

        {/* Long manuscripts */}
        <section className="mt-16" data-testid="section-long-docs">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-10 h-10 rounded-lg border border-border bg-card flex items-center justify-center">
              <BookOpen size={18} weight="duotone" className="text-gold" />
            </div>
            <h3 className="text-2xl font-serif font-normal text-ivory tracking-tight">For long manuscripts</h3>
          </div>
          <div className="space-y-4 text-bone font-sans leading-[1.85]">
            <p>
              For longer documents, Manuscript Polish processes your manuscript in sections so you can see clear progress while it works.
            </p>
            <p>
              When your file is large, you may see updates like <em className="text-ivory not-italic">"Polishing chapter 2 of 5…"</em> or <em className="text-ivory not-italic">"Checking chunk 3 of 8…"</em>. That means the app is still working on your text, and you can follow along as each section is completed. A progress bar at the top of the editor moves forward with each completed section.
            </p>
            <p>
              This approach keeps the experience transparent and reassuring, especially when you're uploading a full novel or a large screenplay. Rather than leaving you guessing, it shows where your manuscript is in the process and helps the wait feel more manageable.
            </p>
          </div>
        </section>

        {/* Songs */}
        <section className="mt-16" data-testid="section-songs">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-10 h-10 rounded-lg border border-border bg-card flex items-center justify-center">
              <MusicNote size={18} weight="duotone" className="text-gold" />
            </div>
            <h3 className="text-2xl font-serif font-normal text-ivory tracking-tight">For songwriters</h3>
          </div>
          <div className="space-y-4 text-bone font-sans leading-[1.85]">
            <p>
              Select <em className="text-ivory not-italic">Song</em> from the Type drop-down menu.
            </p>
            <p>
              Upload your lyrics and Manuscript Polish will return a written analysis with suggestions on <em className="text-ivory not-italic">theme, structure, repetition, tone, and impact</em>, plus untypical rhyme alternatives where they can elevate the song. {isAdmin ? <span className="text-emerald-300 font-medium">Complimentary for the host.</span> : <>Each song is priced at <span className="text-gold font-medium">£1</span>.</>}
            </p>
          </div>
        </section>

        {/* Pricing (hidden for admin — host services are complimentary) */}
        {isAdmin ? (
          <section className="mt-16" data-testid="admin-complimentary-panel">
            <div className="panel-luxe p-8 flex items-start gap-4 border border-emerald-400/30 bg-emerald-400/5">
              <Sparkle size={22} weight="duotone" className="text-emerald-300 shrink-0 mt-1" />
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-emerald-300 mb-2">Host · Complimentary</p>
                <h3 className="text-xl font-serif font-normal text-ivory mb-2">All services are free for you</h3>
                <p className="text-sm text-bone font-sans leading-relaxed">
                  As the host of Manuscript Polish, every service — Proofreading, Song Analysis, Rewrite,
                  Word Extensions, Monthly Subscriptions and Creative Partner — is complimentary. Pricing is
                  hidden on your account so you can focus on the work.
                </p>
              </div>
            </div>
          </section>
        ) : (
        <section className="mt-16" data-testid="section-pricing">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 rounded-lg border border-border bg-card flex items-center justify-center">
              <CurrencyGbp size={18} weight="duotone" className="text-gold" />
            </div>
            <h3 className="text-2xl font-serif font-normal text-ivory tracking-tight">Pricing in full</h3>
          </div>

          <p className="text-bone font-sans leading-[1.85] mb-8 max-w-2xl">
            Manuscript Polish is built so you can polish a chapter for the price of a coffee, or run an entire novel through it once at a flat manuscript rate. There are no hidden charges. Everything below is in <span className="text-ivory">GBP</span>.
          </p>

          {/* Free trial */}
          <div className="panel-luxe p-6 mb-5" data-testid="pricing-free-trial">
            <div className="flex items-start gap-4">
              <Star size={20} weight="duotone" className="text-gold shrink-0 mt-1" />
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1">Welcome trial</div>
                <p className="font-serif text-xl text-ivory mb-1">3,000 free words · £0</p>
                <p className="text-sm text-bone leading-relaxed">
                  Every new account starts with 3,000 free welcome words — enough for a long short story, a screenplay scene, or several song analyses. No card required, no expiry.
                </p>
              </div>
            </div>
          </div>

          {/* Per-document tiers */}
          <h4 className="text-[10px] font-medium uppercase tracking-[0.32em] text-ash mt-10 mb-4">Per-document tiers (after the free trial)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-tiers">
            <div className="panel-luxe p-6">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Small</p>
              <p className="font-serif text-3xl text-ivory mb-1">£5</p>
              <p className="text-xs text-ash mb-3">Up to 2,000 words</p>
              <p className="text-sm text-bone leading-relaxed">Short stories, scenes, blog posts, a single chapter.</p>
            </div>
            <div className="panel-luxe p-6 border-gold/50">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Standard</p>
              <p className="font-serif text-3xl text-ivory mb-1">£10</p>
              <p className="text-xs text-ash mb-3">2,001 – 5,000 words</p>
              <p className="text-sm text-bone leading-relaxed">Novella chapters, short screenplays, longer essays.</p>
            </div>
            <div className="panel-luxe p-6">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Large</p>
              <p className="font-serif text-3xl text-ivory mb-1">£15 + £3</p>
              <p className="text-xs text-ash mb-3">5,000 words base · +£3 per extra 1,000 words</p>
              <p className="text-sm text-bone leading-relaxed">
                Full novels, feature screenplays, anthologies. Example: a 70,000-word manuscript = £15 + (65 × £3) = £210.
              </p>
            </div>
          </div>

          {/* Songs */}
          <div className="panel-luxe p-6 mt-5" data-testid="pricing-songs">
            <div className="flex items-start gap-4">
              <MusicNote size={20} weight="duotone" className="text-gold shrink-0 mt-1" />
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1">Songs · flat rate</div>
                <p className="font-serif text-xl text-ivory mb-1">£1 per song</p>
                <p className="text-sm text-bone leading-relaxed">
                  Lyrics are kept verbatim — no grammar corrections — and a full structural analysis is returned. The £1 is charged per song regardless of length, with no impact on your free-word balance.
                </p>
              </div>
            </div>
          </div>

          {/* Priority */}
          <h4 className="text-[10px] font-medium uppercase tracking-[0.32em] text-ash mt-10 mb-4">Priority (optional surcharge)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-priority">
            <div className="panel-luxe p-5">
              <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} weight="duotone" className="text-gold" /> <span className="text-sm text-ivory">Standard</span></div>
              <p className="text-xs text-ash">Normal queue · no surcharge</p>
            </div>
            <div className="panel-luxe p-5">
              <div className="flex items-center gap-2 mb-2"><LightningIcon size={16} weight="duotone" className="text-gold" /> <span className="text-sm text-ivory">Rush · +25%</span></div>
              <p className="text-xs text-ash">Bumped ahead of standard jobs</p>
            </div>
            <div className="panel-luxe p-5">
              <div className="flex items-center gap-2 mb-2"><LightningIcon size={16} weight="duotone" className="text-gold" /> <span className="text-sm text-ivory">Express · +50%</span></div>
              <p className="text-xs text-ash">Top of every queue</p>
            </div>
          </div>

          {/* Extensions */}
          <h4 className="text-[10px] font-medium uppercase tracking-[0.32em] text-ash mt-10 mb-4">Free-word extensions (one-off top-ups)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-extensions">
            <div className="panel-luxe p-5">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Small</p>
              <p className="font-serif text-2xl text-ivory mb-1">£3</p>
              <p className="text-xs text-ash">+2,500 free words</p>
            </div>
            <div className="panel-luxe p-5 border-gold/50">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Medium</p>
              <p className="font-serif text-2xl text-ivory mb-1">£5</p>
              <p className="text-xs text-ash">+5,000 free words</p>
            </div>
            <div className="panel-luxe p-5">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Large</p>
              <p className="font-serif text-2xl text-ivory mb-1">£10</p>
              <p className="text-xs text-ash">+10,000 free words</p>
            </div>
          </div>
          <p className="text-xs text-ash mt-3 leading-relaxed">
            Extensions never expire and apply to any document type. Pay-as-you-go (£3 per 1,000 words) is also available inside the Top-Up dialog.
          </p>

          {/* Subscriptions */}
          <h4 className="text-[10px] font-medium uppercase tracking-[0.32em] text-ash mt-10 mb-4">Monthly subscriptions</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pricing-subscriptions">
            <div className="panel-luxe p-6">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Starter</p>
              <p className="font-serif text-3xl text-ivory mb-1">£8<span className="text-sm text-ash">/mo</span></p>
              <p className="text-xs text-ash mb-3">10,000 words / month</p>
              <ul className="text-sm text-bone space-y-1.5">
                <li>• Both AI models</li>
                <li>• All file formats</li>
                <li>• Email support</li>
              </ul>
            </div>
            <div className="panel-luxe p-6 border-gold/50">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Pro</p>
              <p className="font-serif text-3xl text-ivory mb-1">£15<span className="text-sm text-ash">/mo</span></p>
              <p className="text-xs text-ash mb-3">25,000 words / month</p>
              <ul className="text-sm text-bone space-y-1.5">
                <li>• Everything in Starter</li>
                <li>• Rush priority included</li>
                <li>• Priority support</li>
              </ul>
            </div>
            <div className="panel-luxe p-6">
              <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-2">Unlimited</p>
              <p className="font-serif text-3xl text-ivory mb-1">£30<span className="text-sm text-ash">/mo</span></p>
              <p className="text-xs text-ash mb-3">100,000 words / month</p>
              <ul className="text-sm text-bone space-y-1.5">
                <li>• Everything in Pro</li>
                <li>• Express priority included</li>
                <li>• Dedicated support</li>
              </ul>
            </div>
          </div>

          {/* Promo codes */}
          <div className="panel-luxe p-6 mt-10" data-testid="pricing-promo">
            <div className="flex items-start gap-4">
              <Ticket size={20} weight="duotone" className="text-gold shrink-0 mt-1" />
              <div>
                <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold mb-1">Promo codes</div>
                <p className="text-sm text-bone leading-relaxed">
                  If you have a promo code, enter it in the price preview before checkout. Discounts can be percentage-based (10 %, 25 %, 100 %), capped to a maximum number of redemptions, and time-limited. A 100 % code starts your proofread instantly with no payment screen.
                </p>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-ash mt-8 leading-relaxed max-w-2xl">
            All prices in GBP, inclusive of VAT where applicable. Payments processed securely by Stripe. You only ever pay per document — there are no hidden subscriptions unless you choose one.
          </p>
        </section>
        )}

        <div className="mt-16">
          <Button
            onClick={() => navigate('/')}
            className="rounded-lg font-medium text-sm tracking-wide px-8 py-4 bg-gold text-charcoal hover:bg-gold-dark"
            data-testid="get-started-btn"
          >
            Get Started →
          </Button>
        </div>
      </main>
      <BackToTop />
    </div>
  );
};
